import React, { useEffect, useRef, useState } from "react";
import { Check, Clock, Copy, Download, MessageSquare, Send, X } from "lucide-react";
import { api } from "../api";
import { formatBytes, safeFileName } from "../util";
import { FileIcon, getFileCategory } from "./FileIcon";

export interface PreviewItem {
  fileId?: string;
  transferId?: string;
  token?: string;
  name: string;
  size: number;
  type: string;
  blob: Blob;
}

interface ReviewComment {
  id: number;
  fileId: string | null;
  timestampSec: number | null;
  authorName: string;
  commentText: string;
  createdAt: number;
}

function formatTimecode(sec: number | null): string {
  if (sec === null || !Number.isFinite(sec)) return "General";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const tenths = Math.floor((sec % 1) * 10);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${tenths}`;
}

interface MediaModalProps {
  item: PreviewItem;
  onClose: () => void;
  onDownload: () => void;
}

export function MediaModal({ item, onClose, onDownload }: MediaModalProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { category } = getFileCategory(item.name, item.type);

  // Frame-Accurate Review State
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [comments, setComments] = useState<ReviewComment[]>([]);
  const [authorName, setAuthorName] = useState("Client Reviewer");
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);

  const isTimeBased = category === "video" || category === "audio";

  useEffect(() => {
    const url = URL.createObjectURL(item.blob);
    setObjectUrl(url);

    if (category === "code" || category === "document" || item.type.startsWith("text/")) {
      item.blob.text().then((txt) => setTextContent(txt.slice(0, 100_000))).catch(() => undefined);
    }

    if (item.transferId && item.token) {
      api
        .getComments(item.transferId, item.token)
        .then((r) => setComments(r.comments.filter((c) => !item.fileId || !c.fileId || c.fileId === item.fileId)))
        .catch(() => undefined);
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
      URL.revokeObjectURL(url);
    };
  }, [item, category, onClose]);

  const seekTo = (sec: number | null) => {
    if (sec === null) return;
    if (videoRef.current) {
      videoRef.current.currentTime = sec;
      videoRef.current.pause();
    }
    if (audioRef.current) {
      audioRef.current.currentTime = sec;
      audioRef.current.pause();
    }
    setCurrentTime(sec);
  };

  const handlePostComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || !item.transferId || !item.token) return;
    setPosting(true);
    try {
      const res = await api.addComment(item.transferId, item.token, {
        ...(item.fileId ? { fileId: item.fileId } : {}),
        timestampSec: isTimeBased ? Number(currentTime.toFixed(1)) : null,
        authorName: authorName.trim() || "Reviewer",
        commentText: commentText.trim(),
      });
      setComments((prev) => [...prev, res.comment]);
      setCommentText("");
    } catch {
      /* ignore */
    } finally {
      setPosting(false);
    }
  };

  const copyText = async () => {
    if (!textContent) return;
    await navigator.clipboard.writeText(textContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-box">
            <FileIcon name={item.name} type={item.type} size={20} />
            <div className="modal-title-text">
              <span className="modal-filename" title={item.name}>
                {safeFileName(item.name)}
              </span>
              <span className="modal-filesize">{formatBytes(item.size)} · Zero-Knowledge Frame Review</span>
            </div>
          </div>
          <div className="modal-actions">
            {textContent !== null && (
              <button className="btn ghost btn-sm" onClick={copyText} title="Copy file contents">
                {copied ? <Check size={14} color="var(--lime)" /> : <Copy size={14} />}
                <span>{copied ? "Copied" : "Copy"}</span>
              </button>
            )}
            <button className="btn btn-sm" onClick={onDownload} title="Save to disk">
              <Download size={14} />
              <span>Save</span>
            </button>
            <button className="btn ghost btn-icon" onClick={onClose} aria-label="Close modal">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="modal-body" style={{ flexDirection: "column", alignItems: "stretch" }}>
          {category === "image" && objectUrl && (
            <div className="preview-image-wrap">
              <img src={objectUrl} alt={item.name} className="preview-img" />
            </div>
          )}

          {category === "video" && objectUrl && (
            <div className="preview-media-wrap">
              <video
                ref={videoRef}
                src={objectUrl}
                controls
                autoPlay
                className="preview-video"
                onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
              />
            </div>
          )}

          {category === "audio" && objectUrl && (
            <div className="preview-audio-wrap">
              <div className="audio-visualizer-wave">
                <div className="bar b1" />
                <div className="bar b2" />
                <div className="bar b3" />
                <div className="bar b4" />
                <div className="bar b5" />
              </div>
              <audio
                ref={audioRef}
                src={objectUrl}
                controls
                autoPlay
                className="preview-audio"
                onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
              />
            </div>
          )}

          {(category === "code" || category === "document" || item.type.startsWith("text/")) && (
            <div className="preview-text-wrap">
              {textContent !== null ? (
                <pre className="preview-code">
                  <code>{textContent}</code>
                </pre>
              ) : (
                <div className="preview-loading">Reading text preview…</div>
              )}
            </div>
          )}

          {/* Frame-Accurate Review & Commenting Bar (Frame.io Killer) */}
          {item.transferId && item.token && (
            <div style={{ marginTop: 16, padding: 14, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <b style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                  <MessageSquare size={14} color="var(--lime)" /> Frame-Accurate Review Notes ({comments.length})
                </b>
                {isTimeBased && (
                  <span className="badge" style={{ fontFamily: "ui-monospace, monospace", fontSize: 11 }}>
                    <Clock size={11} /> Playhead: {formatTimecode(currentTime)}
                  </span>
                )}
              </div>

              <form onSubmit={handlePostComment} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: comments.length ? 12 : 0 }}>
                <input
                  className="textinput"
                  style={{ width: 140, marginTop: 0 }}
                  placeholder="Your name"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                />
                <input
                  className="textinput"
                  style={{ flex: 1, minWidth: 200, marginTop: 0 }}
                  placeholder={isTimeBased ? `Add review note at ${formatTimecode(currentTime)} (e.g. 'Adjust color grade here')…` : "Add review note…"}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                />
                <button className="btn btn-sm" type="submit" disabled={posting || !commentText.trim()}>
                  <Send size={13} /> Post Note
                </button>
              </form>

              {comments.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 150, overflowY: "auto" }}>
                  {comments.map((c) => (
                    <div key={c.id} className="file" style={{ padding: "8px 10px", fontSize: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {c.timestampSec !== null && (
                          <button
                            type="button"
                            className="badge"
                            style={{ cursor: "pointer", fontFamily: "ui-monospace, monospace", padding: "2px 7px" }}
                            onClick={() => seekTo(c.timestampSec)}
                            title="Jump to this exact frame"
                          >
                            ⏱ {formatTimecode(c.timestampSec)}
                          </button>
                        )}
                        <b>{c.authorName}:</b>
                        <span>{c.commentText}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
